import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

export default function useItemDetail(initialItems = []) {
  const formContext = useFormContext();

  const getCurrentItems = () => {
    const liveItems = formContext?.getValues('items');
    if (Array.isArray(liveItems) && liveItems.length > 0) {
      return liveItems;
    }
    return initialItems.length > 0
      ? initialItems
      : [{ id: Date.now(), name: '', description: '', price: '', image: null }];
  };

  const [itemsState, setItemsState] = useState(getCurrentItems);

  const watchedItems = formContext?.watch('items');
  const currentItems = (Array.isArray(watchedItems) && watchedItems.length > 0)
    ? watchedItems
    : itemsState;

  const updateItems = (newItems) => {
    setItemsState(newItems);
    if (formContext?.setValue) {
      formContext.setValue('items', newItems, { shouldValidate: true });
    }
  };

  const handleAddItem = () => {
    const latest = getCurrentItems();
    updateItems([
      ...latest,
      { id: Date.now(), name: '', description: '', price: '', image: null },
    ]);
  };

  const handleDeleteItem = (index) => {
    const latest = getCurrentItems();
    updateItems(latest.filter((_, i) => i !== index));
  };

  const handleDuplicateItem = (index) => {
    const latest = getCurrentItems();
    const itemToDuplicate = latest[index];
    if (!itemToDuplicate) return;

    const clonedItem = JSON.parse(JSON.stringify(itemToDuplicate));
    clonedItem.id = Date.now();

    const newItems = [...latest];
    newItems.splice(index + 1, 0, clonedItem);
    updateItems(newItems);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const latest = getCurrentItems();
    const newItems = [...latest];
    const temp = newItems[index];
    newItems[index] = newItems[index - 1];
    newItems[index - 1] = temp;
    updateItems(newItems);
  };

  const handleMoveDown = (index) => {
    const latest = getCurrentItems();
    if (index === latest.length - 1) return;
    const newItems = [...latest];
    const temp = newItems[index];
    newItems[index] = newItems[index + 1];
    newItems[index + 1] = temp;
    updateItems(newItems);
  };

  const handleAddItemsFromCatalogue = (catalogueItems) => {
    if (!Array.isArray(catalogueItems) || catalogueItems.length === 0) return;

    const latest = getCurrentItems();
    const mappedItems = catalogueItems.map((item, offset) => ({
      id: Date.now() + offset,
      name: item.name || '',
      description: item.description || '',
      price: item.price ?? '',
      image: item.photoUrls?.[0] || null,
      maxQuantity: item.maxQuantity,
      limitPerOrder: item.limitPerOrder,
      minOrderQty: item.minOrderQty,
      subItems: item.subItems || [],
      splitCasePurchase: item.splitCasePurchase || null,
    }));

    updateItems([...latest, ...mappedItems]);
  };

  return {
    items: currentItems,
    setItems: updateItems,
    handleAddItem,
    handleDeleteItem,
    handleDuplicateItem,
    handleMoveUp,
    handleMoveDown,
    handleAddItemsFromCatalogue,
  };
}
